/*
 * CAPTURE-OWNED MODULE
 * FieldPropertiesPanel — redesigned right-panel field properties
 *
 * Design goals:
 *  - Show the extracted value prominently as the "hero" element
 *  - Hide the internal zone machinery unless the user explicitly expands it
 *  - Plain-English extraction summary ("Looks for X, extracts the value to its right")
 *  - Inline anchor text editing without exposing "anchor" terminology
 *  - Advanced / technical details collapsed behind a toggle
 */

import React, { useState, useCallback, useEffect } from 'react';
import AnchorMatchSettings from './AnchorMatchSettings';
import { isPositionOnly, POSITION_ONLY_LABEL, POSITION_ONLY_TITLE } from '../../utils/positionOnly';
import { captureAPI } from '../../services/api';
import './FieldPropertiesPanel.css';

// ── Re-resolve anchor: local reason / error text helpers ──────────────────────
// (mirror TemplateStudioAssistantPanel's previewReasonText / errorText)
function anchorReasonText(reason) {
  switch (reason) {
    case 'anchor_not_found':
      return "That label wasn't found on the source document.";
    case 'no_field_zone':
      return 'This field has no zone to re-anchor.';
    case 'field_not_found':
      return 'Field not found on this template.';
    case 'no_source_ocr':
      return 'The source document has no OCR to resolve against.';
    default:
      return `No preview available (${reason}).`;
  }
}

function anchorErrorText(err) {
  return (
    err?.response?.data?.error ||
    err?.message ||
    'Something went wrong. Please try again.'
  );
}

// ── Confidence badge ──────────────────────────────────────────────────────────
function ConfidenceBadge({ score }) {
  if (score == null) return null;
  const pct = Math.round(score * 100);
  const level = pct >= 85 ? 'high' : pct >= 60 ? 'medium' : 'low';
  return (
    <span className={`fpp-confidence fpp-confidence-${level}`}>
      {pct}%
    </span>
  );
}

// ── Extraction summary sentence ───────────────────────────────────────────────
function extractionSummary(field, anchorZone, fieldZone) {
  const label = field.label || field.field_key;
  if (anchorZone?.anchor_text && fieldZone) {
    return `Looks for "${anchorZone.anchor_text}" on the document, then extracts the value next to it.`;
  }
  if (anchorZone?.anchor_text) {
    return `Looks for "${anchorZone.anchor_text}" to locate this field.`;
  }
  if (fieldZone) {
    return `Extracts ${label} from a fixed zone on page ${(fieldZone.page_index ?? 0) + 1}.`;
  }
  return `No extraction zone defined. Draw a zone on the document to map this field.`;
}

// ── Zone type pill ────────────────────────────────────────────────────────────
function ZoneTypePill({ type }) {
  const labels = { region: 'Search Area', anchor: 'Label', field: 'Value', line_items_table: 'Table' };
  return (
    <span className={`fpp-zone-pill fpp-zone-${type}`}>
      {labels[type] || type}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const FieldPropertiesPanel = ({
  field,              // selected field object
  zones,              // all zones array
  ocrWords,           // OCR word list for text preview computation
  computeZoneText,    // (zone, ocrWords) => { text, wordCount }
  onZoneAnchorUpdate, // (zoneId, updates) => void
  onZoneDelete,       // (zoneId) => void
  onFieldDelete,      // (fieldId) => void
  showAdvanced,       // boolean — controlled by parent Advanced View toggle
  templateId,         // template id for preview/apply calls
  documentId,         // sample doc id for the non-persisting preview
  onAnchorReplaced,   // () => void — parent re-fetches zones after a replace
}) => {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Re-resolve anchor position: collapsible correct→preview→apply affordance.
  const [reresolveOpen, setReresolveOpen] = useState(false);
  const [anchorInput, setAnchorInput] = useState('');
  const [anchorPreviewState, setAnchorPreviewState] = useState(null);
  const [anchorApplyState, setAnchorApplyState] = useState(null);
  const [lastFieldId, setLastFieldId] = useState(null);

  // Reset advanced panel when field changes
  useEffect(() => { setAdvancedOpen(false); }, [field?.id]);

  if (!field) return null;

  // ── Gather this field's zones ──────────────────────────────────────────────
  const fieldZones = zones.filter(z => z.field_id === field.id);
  const anchorZone  = fieldZones.find(z => z.zone_type === 'anchor');
  const valueZone   = fieldZones.find(z => z.zone_type === 'field');
  const regionZone  = fieldZones.find(z => z.zone_type === 'region');

  // ── Compute extracted value from the field (value) zone ───────────────────
  const valuePreview = valueZone
    ? computeZoneText(valueZone, ocrWords)
    : null;

  // ── Anchor OCR preview (for AnchorMatchSettings) ──────────────────────────
  const anchorPreview = anchorZone
    ? computeZoneText(anchorZone, ocrWords)
    : null;

  // ── Zone coverage status ───────────────────────────────────────────────────
  const hasMappedZone = fieldZones.length > 0;
  const hasValueZone  = !!valueZone;

  const summary = extractionSummary(field, anchorZone, valueZone);

  // ── Re-resolve anchor: reset section state when the selected field changes ──
  // (setState-during-render, guarded by field id so it runs once per selection;
  //  keying on field id — not zones — keeps the "Applied" note after the parent
  //  re-fetches zones.)
  if (field.id !== lastFieldId) {
    setLastFieldId(field.id);
    setReresolveOpen(false);
    setAnchorInput(anchorZone?.anchor_text ?? '');
    setAnchorPreviewState(null);
    setAnchorApplyState(null);
  }

  // The anchor carries advanced match settings that replace_anchor will clear.
  const anchorHasAdvanced = !!(
    anchorZone &&
    (anchorZone.anchor_regex ||
      (Array.isArray(anchorZone.anchor_alternatives) && anchorZone.anchor_alternatives.length > 0) ||
      anchorZone.anchor_match_mode === 'fuzzy')
  );

  const previewAnchorText = String(anchorPreviewState?.before ?? '');
  const previewAnchorAfter = String(anchorPreviewState?.after ?? '');
  const previewNoChange =
    anchorPreviewState?.status === 'done' &&
    !anchorPreviewState.reason &&
    previewAnchorText === previewAnchorAfter;

  const doAnchorPreview = async () => {
    const text = anchorInput.trim();
    if (!documentId || !text || anchorPreviewState?.status === 'loading') return;
    setAnchorPreviewState({ status: 'loading' });
    try {
      const data = await captureAPI.assistantPreviewAnchor({
        templateId,
        documentId,
        field_key: field.field_key,
        anchor_text: text,
        anchor_match_mode: anchorZone?.anchor_match_mode,
        replace: true,
      });
      if (data && data.ok) {
        setAnchorPreviewState({ status: 'done', before: data.before, after: data.after });
      } else {
        setAnchorPreviewState({ status: 'done', reason: data?.reason || 'no_preview' });
      }
    } catch (err) {
      setAnchorPreviewState({ status: 'error', error: anchorErrorText(err) });
    }
  };

  const doAnchorApply = async () => {
    const text = anchorInput.trim();
    if (!text || anchorApplyState?.status === 'applying') return;
    setAnchorApplyState({ status: 'applying' });
    try {
      const data = await captureAPI.assistantApply({
        templateId,
        proposal: {
          type: 'replace_anchor',
          field_key: field.field_key,
          anchor_text: text,
          anchor_match_mode: anchorZone?.anchor_match_mode,
        },
      });
      if (!data || data.ok === false) {
        throw new Error(data?.error || 'The change could not be applied.');
      }
      setAnchorApplyState({ status: 'applied' });
      if (onAnchorReplaced) onAnchorReplaced();
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error || '';
      if (status === 409 || /no_anchor_to_replace/i.test(code)) {
        setAnchorApplyState({ status: 'note', note: 'This field has no anchor to replace.' });
      } else if (status === 422 || /anchor_not_found/i.test(code)) {
        setAnchorApplyState({ status: 'note', note: anchorReasonText('anchor_not_found') });
      } else {
        setAnchorApplyState({ status: 'error', error: anchorErrorText(err) });
      }
    }
  };

  return (
    <div className="fpp-container">

      {/* ── Field title ── */}
      <div className="fpp-header">
        <div className="fpp-title-row">
          <span className="fpp-field-icon">
            {field.value_type === 'date' ? '📅'
              : field.value_type === 'currency' || field.value_type === 'number' ? '🔢'
              : '🔤'}
          </span>
          <h4 className="fpp-field-name">{field.label || field.field_key}</h4>
          {field.required && <span className="fpp-required-badge">Required</span>}
          {isPositionOnly(fieldZones) && (
            <span className="fpp-position-only-badge" title={POSITION_ONLY_TITLE}>{POSITION_ONLY_LABEL}</span>
          )}
        </div>
        <p className="fpp-field-key">{field.field_key}</p>
      </div>

      {/* ── Extracted value — the hero ── */}
      <div className={`fpp-value-card ${!hasValueZone ? 'fpp-value-card-empty' : valuePreview?.text ? 'fpp-value-card-filled' : 'fpp-value-card-no-text'}`}>
        <div className="fpp-value-label">Extracted Value</div>
        {!hasMappedZone ? (
          <div className="fpp-value-empty">
            <span className="fpp-value-empty-icon">⚠</span>
            No zone mapped — draw a zone on the document
          </div>
        ) : !hasValueZone ? (
          <div className="fpp-value-empty">
            <span className="fpp-value-empty-icon">⚠</span>
            No value zone defined — only a {anchorZone ? 'label' : 'region'} zone exists
          </div>
        ) : valuePreview?.text ? (
          <div className="fpp-value-text">{valuePreview.text}</div>
        ) : (
          <div className="fpp-value-empty fpp-value-empty-soft">
            Zone is mapped but no OCR text found in this area
          </div>
        )}
      </div>

      {/* ── Extraction summary ── */}
      <div className="fpp-summary">
        <span className="fpp-summary-icon">ℹ</span>
        <p className="fpp-summary-text">{summary}</p>
      </div>

      {/* ── Anchor text edit — plain English ── */}
      {anchorZone && (
        <div className="fpp-anchor-section">
          <label className="fpp-section-label">Label to look for on document</label>
          <AnchorMatchSettings
            zone={anchorZone}
            ocrTextPreview={anchorPreview?.text || ''}
            onUpdate={onZoneAnchorUpdate}
          />
        </div>
      )}

      {/* ── Re-resolve anchor position — correct the label, preview, apply ── */}
      {anchorZone && (
        <div className="fpp-reresolve-section">
          <button
            className="fpp-reresolve-toggle"
            onClick={() => setReresolveOpen(v => !v)}
          >
            <span className="fpp-reresolve-icon">{reresolveOpen ? '▾' : '▸'}</span>
            Re-resolve anchor position
          </button>

          {reresolveOpen && (
            <div className="fpp-reresolve-body">
              <label className="fpp-section-label">Corrected label text</label>
              <input
                className="fpp-reresolve-input"
                type="text"
                value={anchorInput}
                onChange={(e) => setAnchorInput(e.target.value)}
                placeholder="e.g. Payment Terms:"
                disabled={anchorApplyState?.status === 'applying'}
              />

              {anchorPreviewState?.status === 'done' && !anchorPreviewState.reason && (
                <div className="fpp-reresolve-preview">
                  <span className="fpp-reresolve-before">{previewAnchorText || '—'}</span>
                  <span className="fpp-reresolve-arrow">→</span>
                  <span className="fpp-reresolve-after">{previewAnchorAfter || '—'}</span>
                </div>
              )}
              {previewNoChange && (
                <p className="fpp-reresolve-note">
                  No change on this sample — the anchor's effect appears on documents
                  whose layout differs from the template's.
                </p>
              )}
              {anchorPreviewState?.status === 'done' && anchorPreviewState.reason && (
                <p className="fpp-reresolve-note">{anchorReasonText(anchorPreviewState.reason)}</p>
              )}
              {anchorPreviewState?.status === 'error' && (
                <p className="fpp-reresolve-error">{anchorPreviewState.error}</p>
              )}

              {anchorHasAdvanced && (
                <p className="fpp-reresolve-reset-note">
                  Re-resolving resets this to a plain text match — regex / fuzzy /
                  alternatives will be cleared.
                </p>
              )}

              <div className="fpp-reresolve-actions">
                <button
                  className="fpp-reresolve-preview-btn"
                  onClick={doAnchorPreview}
                  disabled={!documentId || !anchorInput.trim() || anchorPreviewState?.status === 'loading'}
                  title={!documentId ? 'Add a sample document to preview' : undefined}
                >
                  {anchorPreviewState?.status === 'loading' ? 'Previewing…' : 'Preview'}
                </button>
                {anchorApplyState?.status === 'applied' ? (
                  <button className="fpp-reresolve-apply-btn" disabled>Applied</button>
                ) : (
                  <button
                    className="fpp-reresolve-apply-btn"
                    onClick={doAnchorApply}
                    disabled={!anchorInput.trim() || anchorApplyState?.status === 'applying'}
                  >
                    {anchorApplyState?.status === 'applying' ? 'Applying…' : 'Apply'}
                  </button>
                )}
              </div>

              {anchorApplyState?.status === 'applied' && (
                <p className="fpp-reresolve-applied">Anchor re-resolved on this field.</p>
              )}
              {anchorApplyState?.status === 'note' && (
                <p className="fpp-reresolve-note">{anchorApplyState.note}</p>
              )}
              {anchorApplyState?.status === 'error' && (
                <p className="fpp-reresolve-error">{anchorApplyState.error}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── No anchor zone: fallback guidance ── */}
      {!anchorZone && hasValueZone && (
        <div className="fpp-no-anchor-hint">
          This field uses a fixed position zone — it extracts from the same location on every document.
          If the layout varies, add a label zone to improve accuracy.
        </div>
      )}

      {/* ── Advanced toggle ── */}
      <button
        className="fpp-advanced-toggle"
        onClick={() => setAdvancedOpen(v => !v)}
      >
        <span className="fpp-advanced-icon">{advancedOpen ? '▾' : '▸'}</span>
        Advanced / Zone Details
        <span className="fpp-zone-count">{fieldZones.length} zone{fieldZones.length !== 1 ? 's' : ''}</span>
      </button>

      {/* ── Advanced: zone list with coordinates ── */}
      {advancedOpen && (
        <div className="fpp-advanced-body">
          {fieldZones.length === 0 ? (
            <p className="fpp-advanced-empty">No zones mapped to this field.</p>
          ) : (
            <div className="fpp-zone-list">
              {fieldZones.map(z => {
                const zoneText = computeZoneText(z, ocrWords);
                return (
                  <div key={z.id} className="fpp-zone-row">
                    <div className="fpp-zone-row-top">
                      <ZoneTypePill type={z.zone_type} />
                      <span className="fpp-zone-page">Page {(z.page_index ?? 0) + 1}</span>
                      <button
                        className="fpp-zone-delete"
                        onClick={() => onZoneDelete(z.id)}
                        title="Delete this zone"
                      >✕</button>
                    </div>
                    <div className="fpp-zone-coords">
                      x {Number(z.x).toFixed(3)} · y {Number(z.y).toFixed(3)} · {Number(z.w).toFixed(3)} × {Number(z.h).toFixed(3)}
                    </div>
                    {zoneText?.text && (
                      <div className="fpp-zone-ocr-text">"{zoneText.text}"</div>
                    )}
                    {z.anchor_text && (
                      <div className="fpp-zone-anchor-text">Anchor: "{z.anchor_text}"</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Type / Required metadata */}
          <div className="fpp-meta-row">
            <span className="fpp-meta-item">Type: <strong>{field.value_type || 'text'}</strong></span>
            <span className="fpp-meta-item">Required: <strong>{field.required ? 'Yes' : 'No'}</strong></span>
          </div>
        </div>
      )}

      {/* ── Delete field ── */}
      <button
        className="fpp-delete-field"
        onClick={() => onFieldDelete(field.id)}
        title="Delete this field and all its zones"
      >
        <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
          <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
        </svg>
        Delete Field
      </button>

    </div>
  );
};

export default FieldPropertiesPanel;
